import {
  startAuthentication,
  startRegistration,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialRequestOptionsJSON,
  PublicKeyCredentialCreationOptionsJSON,
} from '@simplewebauthn/browser';
import { apiGet, apiPost, apiPatch, apiDelete } from './client';
import { setSession, markPasskeyUsed } from '../auth';
import type { TokenPair, Me, PasskeyRow } from './types';

export { browserSupportsWebAuthn, browserSupportsWebAuthnAutofill };

/**
 * Passkey sign-in (public): fetch options → run the browser ceremony → verify →
 * persist the issued token pair. `email` is optional; without it the browser
 * uses a discoverable (resident) credential.
 */
export async function signInWithPasskey(email?: string): Promise<void> {
  const optionsJSON = await apiPost<PublicKeyCredentialRequestOptionsJSON>(
    '/auth/v2/passkey/auth/options',
    email ? { email } : {},
  );
  const response = await startAuthentication({ optionsJSON });
  const pair = await apiPost<TokenPair>('/auth/v2/passkey/auth/verify', {
    response,
  });
  setSession(pair);
  markPasskeyUsed();
}

/**
 * Conditional-UI (autofill) sign-in: no button, no gesture — the browser offers
 * saved passkeys in the email field's autofill and this resolves once one is
 * picked. Starting a modal ceremony auto-cancels this in-flight request.
 */
export async function signInWithPasskeyAutofill(): Promise<void> {
  const optionsJSON = await apiPost<PublicKeyCredentialRequestOptionsJSON>(
    '/auth/v2/passkey/auth/options',
    {},
  );
  const response = await startAuthentication({ optionsJSON, useBrowserAutofill: true });
  const pair = await apiPost<TokenPair>('/auth/v2/passkey/auth/verify', {
    response,
  });
  setSession(pair);
  markPasskeyUsed();
}

/** Enrol a new passkey for the signed-in admin. */
export async function enrollPasskey(nickname?: string): Promise<void> {
  const optionsJSON = await apiPost<PublicKeyCredentialCreationOptionsJSON>(
    '/auth/v2/passkey/register/options',
  );
  const response = await startRegistration({ optionsJSON });
  await apiPost('/auth/v2/passkey/register/verify', {
    response,
    ...(nickname ? { nickname } : {}),
  });
  markPasskeyUsed();
}

export const getMe = (): Promise<Me> => apiGet<Me>('/auth/v2/me');

export const listPasskeys = (): Promise<PasskeyRow[]> =>
  apiGet<PasskeyRow[]>('/auth/v2/passkeys');

export const renamePasskey = (id: string, nickname: string): Promise<PasskeyRow> =>
  apiPatch<PasskeyRow>(`/auth/v2/passkeys/${encodeURIComponent(id)}`, { nickname });

export const removePasskey = (id: string): Promise<unknown> =>
  apiDelete(`/auth/v2/passkeys/${encodeURIComponent(id)}`);

export const revokeAllSessions = (): Promise<unknown> =>
  apiPost('/auth/v2/sessions/revoke-all');
